<template>
    <div class="participant-item"
        @click="goToParticipant"
    >
        <div v-if="isMyItem"
            class="participant-item__my-item"
        >
            {{ $t('rewards.leaderboards.you') }}
        </div>

        <div v-if="trophyImage.icon.length > 0"
            class="participant-item__prize"
        >
            <i :class="`icon-${trophyImage.icon}-trophy trophy-icon--${trophyImage.iconClass}`" />
        </div>

        <div class="participant-item__rank">{{ rank }}</div>

        <div class="participant-item__rank-update">
            <rank-update :change="rankDiffColor" />
        </div>

        <div class="participant-item__user-avatar">
            <user-avatar :userAvatar="avatar"
                :countryCode="country"
            />
        </div>

        <div class="participant-item__username">{{ name }}</div>

        <div class="participant-item__user-points">{{ xp }}</div>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapGetters,
} from 'vuex';

// Components
import userAvatar from '@webapp/components/user-avatar.vue';
import rankUpdate from '@webapp/modules/rewards/components/leaderboards/rank-update.vue';

export default {
    name: 'participant-item',
    components: {
        userAvatar,
        rankUpdate,
    },
    props: {
        leaderboardId: {
            type: String,
            default: '',
        },
        participantUserId: {
            type: String,
            default: '',
        },
        rank: {
            type: Number,
            default: 0,
        },
        oldRank: {
            type: Number,
            default: 0,
        },
        avatar: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: 'Placeholder',
        },
        country: {
            type: String,
            default: 'NotRealCountry',
        },
        xp: {
            type: Number,
            default: 0,
        },
        prizeList: {
            type: Array,
            default: () => ([]),
        },
    },
    computed: {
        ...mapGetters(['userId']),

        ...mapGetters('countries', ['findCountryByIso2']),

        isMyItem() {
            return this.userId === this.participantUserId;
        },

        rankDiff() {
            return this.oldRank === null ? 0 : this.oldRank - this.rank;
        },

        rankDiffColor() {
            if (this.rankDiff === 0) {
                return null;
            }

            if (this.rankDiff > 0) {
                return 'up';
            }

            return 'down';
        },

        trophyImage() {
            const prize = this.prizeList.find((prize) => this.rank >= prize.rankFrom && this.rank <= prize.rankTo) || {};

            if (prize.displayOrder === 1) {
                return {
                    icon: 'star',
                    iconClass: 'gold',
                };
            } else if (prize.displayOrder === 2) {
                return {
                    icon: 'star',
                    iconClass: 'silver',
                };
            } else if (prize.displayOrder === 3) {
                return {
                    icon: 'star',
                    iconClass: 'bronze',
                };
            } else if (prize.displayOrder > 3) {
                return {
                    icon: 'normal',
                    iconClass: 'black',
                };
            }

            return {
                icon: '',
                iconClass: '',
            };
        },
    },
    methods: {
        goToParticipant(e) {
            if (this.leaderboardId && this.isMyItem) {
                this.$router.push({
                    path: `/rewards/leaderboard/${this.leaderboardId}`,
                    query: {
                        findUser: true,
                    },
                });
                e.stopPropagation();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .participant-item {
        display: grid;
        grid-template-columns: 2.8rem 3rem 4rem 3rem 5rem 1fr 1fr;
        grid-template-rows: 4.2rem;
        align-items: center;
        grid-template-areas: "my-item prize rank rank-update user-avatar username user-points";
        background: rgba($athens-gray-color, 0.5);
        border-radius: 9px;
        color: $daintree-color;
        padding: 0.2rem 0;
        font-size: 1.3rem;
        font-weight: 700;

        @include min-width(md) {
            font-size: 1.5rem;
        }

        @include min-width(lg) {
            grid-template-columns: 2.8rem 5rem 4rem 3rem 5rem 1fr 1fr;
        }

        &--my-item-bg {
            background: rgba(0, 122, 255, 0.1);
        }

        &__my-item {
            grid-area: my-item;
            color: $white-color;
            background: $azure-radiance-color;
            border-top-left-radius: 9px;
            border-top-right-radius: 9px;
            font-size: 1rem;
            text-align: center;
            transform: rotate(-90deg);
            padding: 0.5rem;
            margin-left: -1rem;
            margin-top: -0.1rem;
            margin-right: -0.7rem;
        }

        &__prize {
            grid-area: prize;
            justify-self: center;
            font-size: 2.2rem;

            > i {
                vertical-align: middle;
                line-height: 4rem;
            }
        }

        &__user-avatar {
            grid-area: user-avatar;
            width: 3.1rem;
            justify-self: center;
            margin-top: -4px;
        }

        &__rank {
            grid-area: rank;
            font-weight: bold;
            text-align: right;
        }

        &__rank-update {
            grid-area: rank-update;
            display: flex;
            font-weight: bold;
            font-size: 1.2rem;
            place-self: center;
            margin-right: 0.5rem;
            margin-left: 0.3rem;
        }

        &__username {
            grid-area: username;
            font-weight: bold;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            margin-left: 0.8rem;
        }

        &__user-points {
            grid-area: user-points;
            justify-self: right;
            padding-right: 1rem;
        }

        .trophy-icon {
            &--gold {
                color: $golden-grass-color;
            }

            &--silver {
                color: $silver-color;
            }

            &--bronze {
                color: $brandy-punch-color;
            }

            &--black {
                color: $daintree-color;
            }
        }
    }
</style>